// src/components/meta/MetaConnectPanel.tsx
// Panel "Conectar con Meta" — botón que arranca OAuth, lista de páginas
// autorizadas para que el cliente elija cuál usar si hay más de una,
// y estado de conexión con opción de desconectar.
//
// Se monta dentro de TravelAdmin → pestaña Empresa → sub-tab "Meta"
// junto a los IDs manuales (meta_page_id etc), o stand-alone donde
// Tony decida integrarlo.

import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, CheckCircle, Instagram, Link as LinkIcon,
  Loader2, LogOut, RefreshCw,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  ApiError, disconnectMetaOAuth, getMetaAuthStatus, startMetaOAuth,
  type MetaAuthStatus,
} from '@/lib/api_meta_oauth';

type Notifier = (t: 'success' | 'error' | 'info', msg: string) => void;

interface Props {
  /** Notifier style matches TravelAdmin; pásalo desde el parent. */
  notify?: Notifier;
}


export function MetaConnectPanel({ notify }: Props) {
  const note = (t: Parameters<NonNullable<Notifier>>[0], m: string) => notify?.(t, m);

  const [status, setStatus] = useState<MetaAuthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'connect' | 'disconnect' | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await getMetaAuthStatus());
    } catch (e: any) {
      note('error', e instanceof ApiError ? e.message : e?.message || 'Error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleConnect = async () => {
    setBusy('connect');
    try {
      const out = await startMetaOAuth();
      // Meta no admite popups para el consent; navegación full-page.
      window.location.href = out.authorization_url;
    } catch (e: any) {
      setBusy(null);
      note('error', e instanceof ApiError ? e.message : e?.message || 'Error');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('¿Desconectar la cuenta de Meta? Tendrás que volver a autorizar.')) return;
    setBusy('disconnect');
    try {
      await disconnectMetaOAuth();
      note('success', 'Cuenta de Meta desconectada');
      await load();
    } catch (e: any) {
      note('error', e instanceof ApiError ? e.message : e?.message || 'Error');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 justify-center text-white/40 text-sm">
        <Loader2 size={14} className="animate-spin" /> Cargando…
      </div>
    );
  }
  if (!status) return null;

  // Pool is now picked per-Business in Negocios. This panel only handles
  // the Org-level OAuth: authorize / revoke, plus a read-only pool view
  // so the user can verify what's reachable from here.
  const authorized = status.user_token_set && !status.revoked_at;

  // ── Not yet authorized
  if (!authorized) {
    return (
      <div className="max-w-xl space-y-4">
        <div className="o-card space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-400" />
            <h2 className="text-lg font-bold text-white">Meta no conectado</h2>
          </div>
          <p className="text-white/50 text-sm">
            Autoriza la app de Meta a nivel Org para obtener el <strong>pool</strong>{' '}
            de páginas FB + cuentas de Instagram + ad accounts. Después, cada
            Negocio elige del pool en <Link to="/m/empresa/negocios" className="text-[var(--acc1)] hover:underline">Empresa → Negocios</Link>.
          </p>
          {status.error_message && (
            <p className="text-red-400 text-xs">{status.error_message}</p>
          )}
          <button
            onClick={handleConnect}
            disabled={busy === 'connect'}
            className="px-4 py-2 rounded-lg bg-[var(--acc1)]/20 hover:bg-[var(--acc1)]/30 border border-[var(--acc1)]/30 text-[var(--acc1)] flex items-center gap-2 w-fit"
          >
            {busy === 'connect' ? <Loader2 size={14} className="animate-spin" /> : <LinkIcon size={14} />}
            Conectar con Meta
          </button>
          <p className="text-[10px] text-white/30">
            💡 Antes del primer intento: asegúrate que esta cuenta de Facebook
            esté agregada como Tester / Developer en la app Meta de Brainstream.
            Si el consent screen dice "Esta app no está disponible" es porque
            falta ese paso.
          </p>
        </div>
      </div>
    );
  }

  // ── Authorized — show OAuth state + read-only pool
  return (
    <div className="max-w-xl space-y-4">
      <div className="o-card space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <CheckCircle size={18} className="text-emerald-400" />
              <h2 className="text-lg font-bold text-white">Meta autorizado</h2>
            </div>
            <p className="text-white/50 text-xs mt-1">
              Desde {status.connected_at ? new Date(status.connected_at).toLocaleString('es-MX') : '—'}
              {status.last_used_at && <> · Último uso: {new Date(status.last_used_at).toLocaleString('es-MX')}</>}
            </p>
          </div>
          <button
            onClick={handleDisconnect}
            disabled={busy === 'disconnect'}
            className="px-2.5 py-1.5 rounded-md text-[11px] text-red-400 hover:bg-red-500/10 flex items-center gap-1"
          >
            {busy === 'disconnect' ? <Loader2 size={12} className="animate-spin" /> : <LogOut size={12} />}
            Desconectar
          </button>
        </div>

        <div className="grid grid-cols-1 gap-1.5 text-xs">
          <Row label="Pool de páginas disponibles" value={<strong>{status.available_pages.length}</strong>} />
          <Row label="Pool de ad accounts disponibles" value={<strong>{status.available_ad_accounts.length}</strong>} />
          {status.granted_scopes && (
            <Row
              label="Permisos"
              value={
                <span className="text-[10px] text-white/40">
                  {status.granted_scopes.split(',').length} scopes otorgados
                </span>
              }
            />
          )}
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={load}
            className="text-[11px] text-white/40 hover:text-white/60 flex items-center gap-1 w-fit"
          >
            <RefreshCw size={11} /> Refrescar pool
          </button>
          <button
            onClick={handleConnect}
            disabled={busy === 'connect'}
            className="text-[11px] text-white/40 hover:text-white/60 flex items-center gap-1"
            title="Re-autoriza la app para ampliar el pool o renovar scopes"
          >
            <LinkIcon size={11} /> Re-autorizar
          </button>
        </div>
      </div>

      {/* Read-only pool summary + CTA a Negocios */}
      <div className="o-card space-y-2">
        <h3 className="text-white text-sm font-semibold">¿Cómo asignar páginas a negocios?</h3>
        <p className="text-white/50 text-xs">
          La selección de página FB, cuenta IG y ad account ahora vive por <strong>Negocio</strong>.
          Ve a <Link to="/m/empresa/negocios" className="text-[var(--acc1)] hover:underline">Empresa → Negocios</Link>,
          abre el negocio y elige del pool en la tab <em>Cuentas Meta</em>.
        </p>
        {status.available_pages.length > 0 && (
          <details className="mt-2">
            <summary className="text-[11px] text-white/40 cursor-pointer hover:text-white/60">
              Ver pool disponible ({status.available_pages.length} páginas · {status.available_ad_accounts.length} ad accounts)
            </summary>
            <div className="mt-2 space-y-1">
              {status.available_pages.map((p) => (
                <div key={p.id} className="flex items-center gap-1.5 text-[11px] text-white/60 px-2 py-1 rounded bg-white/5">
                  {p.has_instagram && <Instagram size={10} className="text-pink-400 shrink-0" />}
                  <span className="truncate">{p.name}</span>
                  <code className="text-[10px] text-white/30 font-mono ml-auto">{p.id}</code>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, muted = false }: { label: string; value: React.ReactNode; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1 border-b border-white/5 last:border-0">
      <span className="text-white/50">{label}</span>
      <span className={muted ? 'text-white/30' : 'text-white/80'}>{value}</span>
    </div>
  );
}

export default MetaConnectPanel;
